import type { FC } from "react";

import { ListCard } from "@/components/ui/list-card";

import type { Artifact } from "../../caseIde/types";
import type { InsightsFlow } from "../hooks/useInsightsFlow";
import type { KeyFactActions } from "../hooks/useKeyFactActions";
import { awaitsReview, groupBySection, groupInstances } from "../model";
import type { KeyFact } from "../types";
import { FactRow } from "./FactRow";
import { InstancesRow } from "./InstancesRow";

const SectionCount: FC<{ facts: readonly KeyFact[] }> = ({ facts }) => {
  const need = facts.filter(
    (f) => f.status === "needs_input" || f.status === "stale"
  ).length;
  const proposed = facts.filter(awaitsReview).length;
  if (need) return <span className="text-sm text-warn">{need} Need You</span>;
  if (proposed)
    return <span className="text-sm text-fg-3">{proposed} Proposed</span>;
  return <span className="text-sm text-fg-3">All Set</span>;
};

/** Every key fact, one card per section with what in it needs the attorney. */
export const FactReference: FC<{
  individualId: string;
  facts: readonly KeyFact[];
  sections: readonly string[];
  flow: InsightsFlow;
  actions: KeyFactActions;
  onOpen: (artifact: Artifact) => void;
}> = ({ individualId, facts, sections, flow, actions, onOpen }) => (
  <div className="@container/facts flex flex-col gap-3 px-3">
    {groupBySection(facts, sections).map(([section, items]) => (
      <section key={section} aria-labelledby={`kf-section-${section}`}>
        <ListCard>
          <div className="flex items-center justify-between gap-2 px-4 py-2.5">
            <h3
              id={`kf-section-${section}`}
              className="truncate text-base font-medium text-fg"
            >
              {section}
            </h3>
            <SectionCount facts={items} />
          </div>
          <ul className="divide-y divide-line-1">
            {groupInstances(items).map((item) =>
              item.kind === "instances" ? (
                <InstancesRow
                  key={item.key}
                  individualId={individualId}
                  label={item.label}
                  facts={item.facts}
                  flow={flow}
                  actions={actions}
                  onOpen={onOpen}
                />
              ) : (
                <FactRow
                  key={item.fact.key}
                  individualId={individualId}
                  fact={item.fact}
                  flow={flow}
                  actions={actions}
                  onOpen={onOpen}
                />
              )
            )}
          </ul>
        </ListCard>
      </section>
    ))}
  </div>
);
